import React, { useEffect, useMemo, useRef, useState } from 'react';

type WhenVisibleProps = {
  children: () => React.ReactNode;
  once?: boolean;
  threshold?: number;
  root?: Element | null;
  rootMargin?: string;
  orElse?: () => React.ReactNode;
  deps?: React.DependencyList;
} & React.HTMLAttributes<HTMLDivElement>;

export const WhenVisible: React.FC<WhenVisibleProps> = ({
  children,
  once = false,
  threshold = 0,
  root = null,
  rootMargin = '20px',
  deps = [],
  orElse = () => <>&nbsp;</>,
  ...rest
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        const currentVisible = entry.isIntersecting;
        setIsVisible(currentVisible);
        if (once && currentVisible) {
          observer.disconnect();
        }
      },
      {
        root,
        rootMargin,
        threshold
      }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [once, root, rootMargin, threshold]);

  const currentChildren = useMemo(() => {
    return isVisible ? children() : orElse();
  }, [isVisible, ...deps]);

  return (
    <div {...rest} ref={ref}>
      {currentChildren}
    </div>
  );
};
